import * as React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AnnouncementComposer from "./AnnouncementComposer";
import { useReportUnsaved } from "./unsaved-changes";
import EmailPreviewDialog from "./EmailPreviewDialog";
import {
  getNotificationSettings,
  listEmailTemplates,
  previewEmailTemplate,
  resetEmailTemplate,
  saveEmailTemplate,
  saveNotificationSettings,
  sendEmailTemplateTest,
} from "../services/api/email-templates";

const EMAIL_PATTERN = /^[^\s@<>"',;]+@[^\s@<>"',;]+\.[^\s@<>"',;]+$/;

/** Where replies to this league's notification emails go. */
function ReplyToSetting({ leagueId }) {
  const [settings, setSettings] = React.useState(null);
  const [value, setValue] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [loadError, setLoadError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    getNotificationSettings(leagueId)
      .then((data) => {
        if (cancelled) return;
        setSettings(data);
        setValue(data.replyToEmail ?? "");
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : "Could not load the reply-to setting.");
      });
    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  // Reported before the early returns below, so the hook always runs.
  const dirty = settings !== null && value.trim() !== (settings.replyToEmail ?? "");
  useReportUnsaved("reply-to", dirty, { label: "Reply-to email", scope: "tab" });

  if (loadError) return <Alert severity="error">{loadError}</Alert>;
  if (!settings) return null;

  const invalid = value.trim() !== "" && !EMAIL_PATTERN.test(value.trim());

  const onSave = async () => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const updated = await saveNotificationSettings(leagueId, value);
      setSettings(updated);
      setValue(updated.replyToEmail ?? "");
      setNotice("Saved. Replies will go to this address.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="h6" component="h3">
        Replies
      </Typography>
      <TextField
        label="Reply-to email"
        type="email"
        size="small"
        fullWidth
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setNotice("");
        }}
        error={invalid}
        placeholder={settings.defaultReplyTo ?? ""}
        helperText={
          invalid
            ? "Enter a valid email address."
            : settings.defaultReplyTo
              ? `When a player replies to a notification, it goes here. Leave empty to use the owner's email (${settings.defaultReplyTo}).`
              : "When a player replies to a notification, it goes here."
        }
      />
      {error ? (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      {notice ? <Alert severity="success">{notice}</Alert> : null}
      <Box>
        <Button variant="contained" onClick={onSave} disabled={busy || !dirty || invalid}>
          Save
        </Button>
      </Box>
    </Stack>
  );
}

const toDraft = (content) => ({
  subject: content.subject ?? "",
  heading: content.heading ?? "",
  body: content.body ?? "",
  buttonLabel: content.buttonLabel ?? "",
});

const sameDraft = (a, b) =>
  a.subject === b.subject && a.heading === b.heading && a.body === b.body && a.buttonLabel === b.buttonLabel;

const toPayload = (draft) => ({
  subject: draft.subject,
  heading: draft.heading,
  body: draft.body,
  buttonLabel: draft.buttonLabel.trim() ? draft.buttonLabel : null,
});

/** One notification's editor. Keeps its own unsaved draft. */
function TemplateEditor({ leagueId, template, onChanged }) {
  const { category, mergeFields, limits } = template;

  const saved = React.useMemo(() => toDraft(template.current), [template.current]); // eslint-disable-line react-hooks/exhaustive-deps
  const [draft, setDraft] = React.useState(saved);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [preview, setPreview] = React.useState(null);
  const [testResult, setTestResult] = React.useState(null); // { sent, message }
  // For emails whose wording differs by what the player has answered: which version Preview and "Send me a test"
  // show. Only used when the template has both versions.
  const [showAs, setShowAs] = React.useState("NO_RESPONSE");
  // Passed along only for templates that have both versions, so other templates are called exactly as before.
  const answerVariant = template.hasAnswerVariants ? [showAs] : [];
  const [confirmReset, setConfirmReset] = React.useState(false);

  const inputs = React.useRef({});
  const lastFocused = React.useRef("body");

  // When the saved version changes (after save or reset), show it.
  React.useEffect(() => {
    setDraft(saved);
  }, [saved]);

  const dirty = !sameDraft(draft, saved);
  useReportUnsaved(`template:${category}`, dirty, { label: `Email wording: ${template.label}`, scope: "tab" });
  const setField = (field) => (e) => {
    setNotice("");
    setDraft((d) => ({ ...d, [field]: e.target.value }));
  };

  // Adds {{field}} where the cursor is, in whichever box was used last.
  const insertField = (name) => {
    const field = lastFocused.current;
    const el = inputs.current[field];
    const token = `{{${name}}}`;
    setNotice("");
    setDraft((d) => {
      const value = d[field];
      const start = el?.selectionStart ?? value.length;
      const end = el?.selectionEnd ?? value.length;
      return { ...d, [field]: value.slice(0, start) + token + value.slice(end) };
    });
    const caret = (el?.selectionStart ?? 0) + token.length;
    setTimeout(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(caret, caret);
      }
    }, 0);
  };

  const run = async (action) => {
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onSave = () =>
    run(async () => {
      const updated = await saveEmailTemplate(leagueId, category, toPayload(draft));
      onChanged(updated);
      setNotice("Saved. New emails will use this wording.");
    });

  const onReset = () =>
    run(async () => {
      const updated = await resetEmailTemplate(leagueId, category);
      setConfirmReset(false);
      onChanged(updated);
      setNotice("Back to the default wording.");
    });

  const onPreview = () =>
    run(async () => {
      const result = await previewEmailTemplate(leagueId, category, toPayload(draft), ...answerVariant);
      if (!result.valid) {
        setError(result.errors.join(" "));
        return;
      }
      setPreview(result);
    });

  const onTest = () =>
    run(async () => {
      setTestResult(null);
      const result = await sendEmailTemplateTest(leagueId, category, toPayload(draft), ...answerVariant);
      setTestResult(result);
    });

  const field = (name, label, extra = {}) => (
    <TextField
      label={label}
      value={draft[name]}
      onChange={setField(name)}
      onFocus={() => {
        lastFocused.current = name;
      }}
      inputRef={(el) => {
        inputs.current[name] = el;
      }}
      fullWidth
      size="small"
      {...extra}
    />
  );

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {template.description} Click a field to add it where your cursor is. Each one is replaced with the real details
        when the email is sent.
      </Typography>

      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
        {mergeFields.map((f) => (
          <Tooltip key={f.name} title={`${f.description}. Example: ${f.example}`} arrow>
            <Chip
              label={`{{${f.name}}}`}
              size="small"
              variant="outlined"
              onClick={() => insertField(f.name)}
              disabled={busy}
            />
          </Tooltip>
        ))}
      </Stack>

      {field("subject", "Subject", { helperText: `${draft.subject.length}/${limits.subject}` })}
      {field("heading", "Heading", { helperText: `${draft.heading.length}/${limits.heading}` })}
      {field("body", "Message", {
        multiline: true,
        minRows: 6,
        helperText: `Leave a blank line between paragraphs. ${draft.body.length}/${limits.body}`,
      })}
      {field("buttonLabel", "Button text", {
        helperText: template.hasRsvpButtons
          ? "This button opens the player's schedule and appears after the Going and Not going buttons. Leave it empty to hide it."
          : "The button opens the player's schedule. Leave this empty to hide the button.",
      })}
      {template.hasRsvpButtons ? (
        <Typography variant="caption" color="text.secondary">
          Green Going and red Not going buttons are added automatically. Tapping one updates the player&apos;s attendance for
          that game right away.
        </Typography>
      ) : null}

      <Typography variant="caption" color="text.secondary">
        An unsubscribe link is always added at the bottom of every email.
      </Typography>

      {error ? (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      {notice ? <Alert severity="success">{notice}</Alert> : null}
      {testResult ? (
        <Alert severity={testResult.sent ? "success" : "info"} onClose={() => setTestResult(null)}>
          {testResult.message}
        </Alert>
      ) : null}

      {template.hasAnswerVariants ? (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
          <Typography variant="body2" color="text.secondary" id={`show-as-${category}`}>
            Preview and test email as a player who:
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={showAs}
            onChange={(_, value) => {
              if (value) {
                setShowAs(value);
                setTestResult(null);
              }
            }}
            aria-labelledby={`show-as-${category}`}
          >
            <ToggleButton value="NO_RESPONSE">Hasn&apos;t answered</ToggleButton>
            <ToggleButton value="YES">Already going</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      ) : null}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Button variant="contained" onClick={onSave} disabled={busy || !dirty}>
          Save
        </Button>
        <Button variant="outlined" onClick={onPreview} disabled={busy}>
          Preview
        </Button>
        <Button variant="outlined" onClick={onTest} disabled={busy}>
          Send me a test
        </Button>
        {template.isCustomized ? (
          <Button color="inherit" onClick={() => setConfirmReset(true)} disabled={busy}>
            Reset to default
          </Button>
        ) : null}
        {dirty ? (
          <Button color="inherit" onClick={() => setDraft(saved)} disabled={busy}>
            Discard changes
          </Button>
        ) : null}
      </Stack>

      <EmailPreviewDialog
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        subject={preview?.subject}
        html={preview?.html}
        note="Shown with sample details (not a real player)."
      />

      <Dialog open={confirmReset} onClose={() => setConfirmReset(false)}>
        <DialogTitle>Reset to the default wording?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your custom wording for &ldquo;{template.label}&rdquo; will be removed and the default will be used again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReset(false)}>Cancel</Button>
          <Button color="error" onClick={onReset} disabled={busy}>
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

/**
 * Manage League > Notifications. Lets owners and admins change the wording of the emails their league
 * sends. Only text is editable: the layout and unsubscribe link are added by the app.
 */
export default function EmailTemplatesEditor({ leagueId }) {
  const [templates, setTemplates] = React.useState(null);
  const [loadError, setLoadError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    listEmailTemplates(leagueId)
      .then((data) => {
        if (!cancelled) setTemplates(data);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : "Could not load your email templates.");
      });
    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  const replace = (updated) =>
    setTemplates((list) => list.map((t) => (t.category === updated.category ? updated : t)));

  if (loadError) return <Alert severity="error">{loadError}</Alert>;
  if (!templates) return <Typography variant="body2">Loading notifications...</Typography>;

  return (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ py: 0.5, "& .MuiAlert-message": { fontSize: "0.875rem" } }}>
        Notifications are not instant. After something triggers one, like a player signing up, it can take up to 10
        minutes to be sent. Changes made close together, like editing several games, are combined into a single email
        per player.
      </Alert>

      {/* Where replies go and the wording of the automatic emails, together in one card like the Settings tab's. */}
      <Card variant="outlined" className="section-card">
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h5">Notification Settings</Typography>
            <Typography variant="body2" color="text.secondary">
              Choose where replies go and the wording of the notifications your league sends. Anything you leave alone
              uses the default.
            </Typography>

            <ReplyToSetting leagueId={leagueId} />

            <Stack component="section" aria-labelledby="league-notifications-email-heading" spacing={1.5}>
              <Typography id="league-notifications-email-heading" variant="h6" component="h3">
                Email Templates
              </Typography>
              {templates.map((t) => (
                <Accordion key={t.category} variant="outlined" disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography sx={{ fontWeight: 600 }}>{t.label}</Typography>
                      <Chip size="small" label={t.isCustomized ? "Customized" : "Default"} color={t.isCustomized ? "primary" : "default"} variant={t.isCustomized ? "filled" : "outlined"} />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TemplateEditor leagueId={leagueId} template={t} onChanged={replace} />
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Announcements sit in their own card underneath. */}
      <AnnouncementComposer leagueId={leagueId} />
    </Stack>
  );
}
